/*
  Rajarambapu Sahakari Bank QR card for the NEW mold (RSBNewQRCard) - printed at 84.3mm x 133mm.
  RSB has no old-mold "new" card upstream (ops-vision), so this one is built directly
  from the artwork the design team supplied:
    - /assets/RSB/rsb-top.svg      (logo + tagline + divider + the "Scan & Pay" pill)
    - /assets/RSB/rsb-bottom-1.svg (BHIM/UPI + accepted-app logos)
    - /assets/RSB/rsb-bottom.svg   ("Powered By" strip with the bank's Marathi name)
  Sized to match the other new-mold cards (same 380 x 600 px capture as CBINewQRCard).
*/
import React, { useRef } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import QRCodeGenerator from './QRCodeGenerator';
import FitMerchantName from './FitMerchantName';

const RSB_BLUE = '#2C51A3';
const RSB_GOLD = '#F5B41A';

// Diagonal gold-to-blue split, matching the bank's own colour pair used in rsb-top.svg.
const QR_FRAME_GRADIENT = `linear-gradient(135deg, ${RSB_GOLD} 0%, ${RSB_GOLD} 50%, ${RSB_BLUE} 50%, ${RSB_BLUE} 100%)`;

export default function RsbNewScanAndPayCard({ qrString, merchantVpa, merchantName }) {
  const cardRef = useRef(null);

  return (
    <Box
      ref={cardRef}
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: '#ffffff',
        borderRadius: '48px',
        boxShadow: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'hidden',
        boxSizing: 'border-box',
        border: '1px solid grey'
      }}
    >
      {/* Header - logo, tagline, divider and the "Scan & Pay" pill all come
          from one artwork strip */}
      <Box
        component="img"
        src="/assets/RSB/rsb-top.svg"
        alt="Rajarambapu Sahakari Bank Ltd - Scan & Pay"
        sx={{ width: '100%', height: 'auto', flexShrink: 0, display: 'block', mt: 1 }}
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          pb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        {/* Merchant Name */}
        <FitMerchantName
          text={merchantName}
          maxFontSize={14}
          minFontSize={11}
          maxLines={2}
          lineHeight={1.2}
          fontWeight={700}
          color={RSB_BLUE}
          textTransform="uppercase"
          containerSx={{ mt: 0.5, width: '100%' }}
        />

        {/* QR Code - gold/blue diagonal frame around a white box */}
        <Box
          sx={{
            p: '4px',
            mx: 2,
            borderRadius: '16px',
            background: QR_FRAME_GRADIENT,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            boxSizing: 'border-box'
          }}
        >
          <Box
            sx={{
              flex: 1,
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '& svg': { width: '100%', height: 'auto', display: 'block' }
            }}
          >
            <QRCodeGenerator qrString={qrString} size={194} />
          </Box>
        </Box>

        {/* UPI ID */}
        <Typography
          align="center"
          sx={{
            mt: 0.5,
            color: 'black',
            fontWeight: 600,
            fontFamily: 'Montserrat',
            fontSize: 13,
            lineHeight: 1.3,
            wordBreak: 'break-all',
            flexShrink: 0
          }}
        >
          {merchantVpa}
        </Typography>

        <Divider
          sx={{
            mt: 0.75,
            backgroundColor: 'grey',
            height: '1.5px',
            width: '88%',
            flexShrink: 0
          }}
        />

        {/* Footer - BHIM/UPI and the accepted payment apps */}
        <Box
          sx={{
            mt: 1.5,
            width: '100%',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Box
            component="img"
            src="/assets/RSB/rsb-bottom.svg"
            alt="BHIM UPI accepted here"
            sx={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }}
          />
        </Box>


      </Box>
    </Box>
  );
}
